import type { CardView, FundView } from "@highmarch/app";
import { OfferItem } from "@highmarch/domain";
import type { TabView } from "./dealsModel";

/**
 * Cards and the War Chest Fund as words (MP-01/02/03): pure functions from the app's `CardView`/`FundView` to what the
 * Deals pages and the hub chip say. No DOM, no clock (`now` is passed in), no rules: whether a day is claimable, a
 * card can be bought again or a milestone is reached is @highmarch/app `Cards`'s answer; this only words it.
 */

/** The hub chip's painted icon: the Cards tab's own (`icons/deals_cards.png`, tools/art/icons3d/deals.py). */
export const CARDS_CHIP_ICON = "icons/deals_cards.png";

/** The tab strip with the Cards and Fund pages' red dots (card days to claim, fund milestones to claim). Each page's art
 * is its own (`icons/deals_<id>.png`, `deals/page_<id>.webp`), as for every Deals page. */
export function withCardTabs(tabs: readonly TabView[], cards: readonly CardView[], fund: FundView | null): TabView[] {
  return tabs.map((t) => {
    if (t.id === "cards") return { ...t, dot: cards.reduce((n, c) => n + (c.claimable > 0 ? 1 : 0), 0) };
    if (t.id === "fund") return { ...t, label: "Fund", dot: fund?.owed ?? 0 };
    return t;
  });
}

/** "Day 4 of 30". */
export const dayText = (v: CardView): string => `Day ${v.day} of ${v.total}`;
/** The hub chip's short form: "Day 4/30". */
export const dayShort = (v: CardView): string => `Day ${v.day}/${v.total}`;

/** The card the hub chip names: the first with a day to claim, else the first running (catalog order: the Stipend first). */
export function chipCard(cards: readonly CardView[]): CardView | null {
  return cards.find((c) => c.claimable > 0) ?? cards.find((c) => c.running) ?? null;
}

/** The hub chip, as data: hidden (null) when no card runs or waits; CLAIM when a day waits. */
export interface CardChip { readonly ready: boolean; readonly label: string; readonly aria: string; readonly count: number }

export function cardChip(cards: readonly CardView[]): CardChip | null {
  const c = chipCard(cards);
  if (!c) return null;
  const count = cards.filter((x) => x.claimable > 0).length;
  const day = c.running ? `, ${dayText(c).toLowerCase()}` : "";
  return count > 0
    ? { ready: true, label: "CLAIM", count, aria: `${c.name}${day}: claim today's reward` }
    : { ready: false, label: dayShort(c), count: 0, aria: `${c.name}${day}: claimed today · open Cards` };
}

/** What one purchase pays in gems over the card's life ("3,300 gems in all"), 0 when it pays no gems. */
export function lifetimeGems(now: readonly OfferItem[], daily: readonly OfferItem[], days: number): number {
  const g = (items: readonly OfferItem[]) => items.reduce((n, it) => n + (it.kind === "gems" ? it.amount : 0), 0);
  return g(now) + g(daily) * days;
}

const plural = (n: number, one: string): string => `${n} ${one}${n === 1 ? "" : "s"}`;

/** The line under a running card: just its days left (owner 2026-10-19: keep text to a minimum). */
export function cardRuleText(v: CardView): string {
  return v.running ? `${plural(v.daysLeft, "day")} left` : "";
}

/** The kicker over what a claim pays: today's day, or today's and the banked ones. */
export const claimKicker = (v: CardView): string => (v.claimable > 1 ? `To claim · ${v.claimable} days` : "Today");

/** A fund milestone's stop on the track: claimed, ready (owned and reached), reached (not owned yet) or locked. */
export type FundStopState = "claimed" | "ready" | "reached" | "locked";

export function fundStops(f: FundView): { level: number; items: readonly OfferItem[]; state: FundStopState }[] {
  return f.milestones.map((m) => ({ level: m.level, items: m.items, state: m.claimed ? "claimed" : m.reached ? (f.owned ? "ready" : "reached") : "locked" }));
}

/** The fund's gems: in all, and paid so far. */
export function fundGems(f: FundView): { total: number; paid: number } {
  const g = (items: readonly OfferItem[]) => items.reduce((n, it) => n + (it.kind === "gems" ? it.amount : 0), 0);
  return { total: f.milestones.reduce((n, m) => n + g(m.items), 0), paid: f.milestones.reduce((n, m) => n + (m.claimed ? g(m.items) : 0), 0) };
}

/** "10 milestones · 1,000 gems each" (the second part only when every milestone pays the same gems). */
export function fundSubText(f: FundView): string {
  const g = f.milestones.map((m) => m.items.reduce((n, it) => n + (it.kind === "gems" ? it.amount : 0), 0));
  const same = g.length > 0 && g[0] > 0 && g.every((x) => x === g[0]);
  return `${plural(f.milestones.length, "milestone")}${same ? ` · ${g[0].toLocaleString("en-US")} gems each` : ""}`;
}

/** The fund's line: what clearing pays, and what a purchase now pays at once. */
export function fundNote(f: FundView): string {
  const ready = f.milestones.filter((m) => m.reached && !m.claimed).length;
  if (f.owned) return ready > 0 ? `${plural(ready, "milestone")} to claim: tap the glowing ones` : "Yours: each level pays on its first clear";
  return ready > 0 ? `${plural(ready, "milestone")} cleared already: ${ready === 1 ? "it pays" : "they pay"} the moment you buy` : "Each level pays once, on its first clear";
}

/**
 * One row of the fund's track as the bar draws it: its stops' fill, where 0 sits at the row's `from` level and stop i
 * at the centre of column i (the Weekly track's spacing, dealsPages.ts barFill).
 */
export function fundRowFill(levels: readonly number[], from: number, cleared: number): number {
  const n = levels.length;
  if (n === 0) return 0;
  let prev = from;
  for (let i = 0; i < n; i++) {
    if (cleared < levels[i]) return (i + Math.max(0, cleared - prev) / Math.max(1, levels[i] - prev)) / n;
    prev = levels[i];
  }
  return 1;
}
